import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import { CategorieIntrant, CreateCategorieIntrantData, PaginatedResponse } from '../../types';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import CategorieIntrantForm from './CategorieIntrantForm';

interface CategorieIntrantManagementProps {
  isVisible: boolean;
}

const CategorieIntrantManagement: React.FC<CategorieIntrantManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategorie, setEditingCategorie] = useState<CategorieIntrant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  const { data: categoriesData, isLoading } = useQuery<PaginatedResponse<CategorieIntrant>>({
    queryKey: ['admin-categories', searchTerm],
    keepPreviousData: true,
    queryFn: () => adminService.getCategoriesIntrants({ search: searchTerm }).then(res => res.data),
    enabled: isVisible,
  });

  const createCategorieMutation = useMutation({
    mutationFn: (data: CreateCategorieIntrantData) => adminService.createCategorieIntrant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setShowForm(false);
      setEditingCategorie(null);
    },
  });

  const updateCategorieMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateCategorieIntrantData> }) =>
      adminService.updateCategorieIntrant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setShowForm(false);
      setEditingCategorie(null);
    },
  });

  const deleteCategorieMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteCategorieIntrant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminService.toggleCategorieStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
    },
  });

  const handleSubmit = (data: CreateCategorieIntrantData) => {
    if (editingCategorie) {
      updateCategorieMutation.mutate({ id: editingCategorie.id, data });
    } else {
      createCategorieMutation.mutate(data);
    }
  };

  const handleEdit = (categorie: CategorieIntrant) => {
    setEditingCategorie(categorie);
    setShowForm(true);
  };

  const handleDelete = (categorie: CategorieIntrant) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${categorie.nom}" ?`)) {
      deleteCategorieMutation.mutate(categorie.id);
    }
  };

  const handleToggleStatus = (categorie: CategorieIntrant) => {
    toggleStatusMutation.mutate(categorie.id);
  };

  const columns = [
    {
      key: 'nom',
      label: 'Nom',
    },
    {
      key: 'Description',
      label: 'Description',
      render: (_: any, categorie: CategorieIntrant) => categorie.description || '-',
    },
    {
      key: 'Statut',
      label: 'is_active',
      render: (_: any, categorie: CategorieIntrant) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          categorie.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {categorie.is_active ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      key: 'Date de création',
      label: 'created_at',
      render: (_: any, categorie: CategorieIntrant) => new Date(categorie.created_at).toLocaleDateString('fr-FR'),
    },
    {
      key: 'Actions',
      label: 'actions',
      sortable: false,
      render: (_: any, categorie: CategorieIntrant) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(categorie)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(categorie)}
            className={`${categorie.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
            title={categorie.is_active ? 'Désactiver' : 'Activer'}
          >
            {categorie.is_active ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => handleDelete(categorie)}
            className="text-red-600 hover:text-red-900"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Gestion des catégories de stock</h2>
          <p className="mt-1 text-sm text-gray-500">
            Organisez vos intrants par catégories
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCategorie(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouvelle catégorie
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Rechercher une catégorie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
        </div>
      </div>

      <DataTable
        data={categoriesData?.results || []}
        columns={columns}
        loading={isLoading}
      />

      {showForm && (
        <CategorieIntrantForm
          categorie={editingCategorie}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingCategorie(null);
          }}
          loading={createCategorieMutation.isPending || updateCategorieMutation.isPending}
        />
      )}
    </div>
  );
};

export default CategorieIntrantManagement;